'use client';

/**
 * ReferralTimeRangeSelector — page-level "time machine" control.
 *
 * Segmented preset bar (This Month / Last Month / … / All Time) that governs the
 * WHOLE Income Opportunity page. Selecting a preset writes ?period=<key> to the
 * URL (router.replace — we don't spam history with every toggle) which re-runs
 * the server component with the new window, rescoping both the KPI cards and the
 * referred-clients list.
 *
 * State lives in the URL (shareable/bookmarkable, survives refresh) — this
 * component only reflects the active key passed down from the server page.
 */

import React, { useTransition } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { cn } from '@/lib/utils';
import { Spinner } from '@/components/shared/Spinner';
import { REFERRAL_PERIODS, type ReferralPeriod } from '../lib/referral-periods';

interface ReferralTimeRangeSelectorProps {
  /** Currently active period key (from stats.period.key on the server). */
  active: ReferralPeriod;
}

export function ReferralTimeRangeSelector({ active }: ReferralTimeRangeSelectorProps) {
  const router = useRouter();
  const pathname = usePathname();
  const [isPending, startTransition] = useTransition();
  // Track which preset the user clicked so we can show a spinner on it while
  // the server re-renders (the active prop only updates after navigation).
  const [optimistic, setOptimistic] = React.useState<ReferralPeriod | null>(null);

  const selected = optimistic ?? active;

  const handleSelect = (key: ReferralPeriod) => {
    if (key === active) return;
    setOptimistic(key);
    // 'all' is the default — drop the param for a clean URL; otherwise set it.
    const params = new URLSearchParams();
    if (key !== 'all') params.set('period', key);
    const query = params.toString();
    startTransition(() => {
      router.replace(query ? `${pathname}?${query}` : pathname, { scroll: false });
    });
  };

  // Clear the optimistic marker once the server has caught up to the URL.
  React.useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: prop-sync clearing optimistic UI once server active value matches; guarded by equality check so it only fires once
    if (optimistic && optimistic === active) setOptimistic(null);
  }, [active, optimistic]);

  return (
    <div
      role="group"
      aria-label="Time range"
      className="inline-flex flex-wrap items-center gap-1 rounded-2xl bg-white/60 dark:bg-zinc-900/40 backdrop-blur-xl border border-zinc-200 dark:border-zinc-800 p-1"
    >
      {REFERRAL_PERIODS.map(({ key, label }) => {
        const isActive = selected === key;
        const showSpinner = isPending && optimistic === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => handleSelect(key)}
            aria-pressed={isActive}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-semibold transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/50',
              isActive
                ? 'bg-amber-500/15 text-amber-500'
                : 'text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800/60'
            )}
          >
            {showSpinner && <Spinner className="h-3 w-3" />}
            {label}
          </button>
        );
      })}
    </div>
  );
}
